
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCandidates } from "@/hooks/useCandidates";
import { useIsDeveloper } from "@/hooks/useIsDeveloper";
import { CandidateFilters } from "./CandidateFilters";
import { CandidateTable } from "./CandidateTable";
import { EnhancedProcessingStatus } from "./EnhancedProcessingStatus";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, Users } from "lucide-react";
import { mockCandidates } from "@/utils/mock/candidates";
import type { Candidate } from "./types";

const CandidateList = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [processingCandidates, setProcessingCandidates] = useState<Set<string>>(new Set());
  const { data: candidates = [], isLoading, error, refetch } = useCandidates();
  const { data: isDeveloper, isLoading: isDeveloperLoading } = useIsDeveloper();

  console.log('[CandidateList] Developer status:', isDeveloper, 'Candidates count:', candidates.length);

  // Only show mock data if user is confirmed developer AND has no real candidates
  // AND we're not loading (to prevent flashing of mock data)
  const shouldShowMockData = isDeveloper === true && candidates.length === 0 && !isLoading;
  const displayCandidates = shouldShowMockData ? mockCandidates : candidates;

  console.log('[CandidateList] Should show mock data:', shouldShowMockData, 'Display candidates count:', displayCandidates.length);

  const filteredCandidates = displayCandidates.filter((candidate: Candidate) => {
    const matchesSearch =
      !searchQuery ||
      candidate.candidate_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      candidate.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      candidate.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      candidate.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (candidate.skills && candidate.skills.some(skill => 
        skill.toLowerCase().includes(searchQuery.toLowerCase())
      ));

    const matchesStatus = statusFilter === "all" || candidate.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (isLoading || isDeveloperLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Failed to load candidates. Please try refreshing the page.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Candidates
            {shouldShowMockData && (
              <span className="text-sm text-muted-foreground font-normal">
                (Showing demo data)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <CandidateFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
          />

          <EnhancedProcessingStatus 
            processingCandidates={processingCandidates}
            setProcessingCandidates={setProcessingCandidates}
          />

          {filteredCandidates.length === 0 && !shouldShowMockData ? (
            <div className="text-center py-10 text-muted-foreground">
              No candidates found. Start by adding your first candidate!
            </div>
          ) : (
            <CandidateTable candidates={filteredCandidates} />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CandidateList;
